import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import mapboxgl from '!mapbox-gl'
import { TextField, Grid, FormControl, InputLabel, Select, FormHelperText, MenuItem } from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'

import { mapBoxKeys, textFieldTypes, variantTypes, locationsTypes } from '../../../../constants'
import { Logger, LoggerTypes } from '../../../../utils'
import { FormTitle } from '../../../forms'
import { iconNames } from '../../../icons'
import './style.scss'

const useStyles = makeStyles((theme) =>
  createStyles({
    circleStyle: {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.primary.main
    }
  })
)

const defaultValue = {
  id: null,
  name: null,
  description: null,
  type: 'POINT',
  latitude: 43.7384,
  // latitude_metric: 0,
  longitude: 7.4246,
  // longitude_metric: 0,
  quantity: null,
  x_spacing: null,
  y_spacing: null,
  orientation: null
}

/**
 * Generic form to create or edit an attribute `location`
 *
 * @param {func} setOpenDialog State function used to close the container popin.
 * @param {string} title Title of the formulary (can change if it's a create or an update).
 * @param {object} location Location attribute object. (used only while editing an existing location).
 * @param {func} dispatchAction Function to call one the form has been submit.
 * @param {object} errors Error object that contains the mistakes fields by fields.
 */
export const AttributeLocationForm = forwardRef(({ setOpenDialog, title, location, dispatchAction, errors }, ref) => {
  const [id] = useState(location?.id || defaultValue.id)
  const [name, setName] = useState(location?.name || defaultValue.name)
  const [description, setDescription] = useState(location?.description || defaultValue.description)
  const [type, setType] = useState(location?.type || locationsTypes.POINT)
  const [latitude, setLatitude] = useState(location?.latitude || defaultValue.latitude)
  const [longitude, setLongitude] = useState(location?.longitude || defaultValue.longitude)
  // const [latitude_metric, setLatitudeMetric] = useState(location?.x || defaultValue.latitude_metric)
  // const [longitude_metric, setLongitudeMetric] = useState(location?.y || defaultValue.longitude_metric)
  const [quantity, setQuantity] = useState(location?.quantity || defaultValue.quantity)
  const [x_spacing, setXSpacing] = useState(location?.x_spacing || defaultValue.x_spacing)
  const [y_spacing, setYSpacing] = useState(location?.y_spacing || defaultValue.y_spacing)
  const [orientation, setOrientation] = useState(location?.orientation || defaultValue.orientation)

  const { t } = useTranslation()
  const styleClasses = useStyles()
  const mapContainer = useRef(null)
  const map = useRef(null)

  let geojson = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude]
        }
      }
    ]
  }

  const onChangeName = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeName()', { name: _value })
    setName(_value)
  }

  const onChangeDescription = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeDescription()', { description: _value })
    setDescription(_value)
  }

  const onChangeType = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeType()', { type: _value })
    setType(_value)
  }

  const onChangeLatitude = (e) => {
    const _value = e.target.value
    const coords = [longitude, _value]
    geojson.features[0].geometry.coordinates = coords

    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeLatitude()', {
      latitude: _value,
      longitude,
      geojson
    })

    if (_value >= -90 && _value <= 90) {
      setLatitude(_value)
      map.current.getSource('point').setData(geojson)
      map.current.flyTo({
        center: coords,
        speed: 2,
        essential: true
      })
    }
  }

  // const onChangeLatitudeMetric = (e) => {
  //   const _value = e.target.value

  //   Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeLatitudeMetric()', {
  //     latitude_metric: _value
  //   })

  //   setLatitudeMetric(_value)
  // }

  const onChangeLongitude = (e) => {
    const _value = e.target.value
    const coords = [_value, latitude]
    geojson.features[0].geometry.coordinates = coords

    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeLongitude()', {
      latitude,
      longitude: _value,
      geojson
    })

    if (_value >= -180 && _value <= 180) {
      setLongitude(_value)
      map.current.getSource('point').setData(geojson)
      map.current.flyTo({
        center: coords,
        speed: 2,
        essential: true
      })
    }
  }

  // const onChangeLongitudeMetric = (e) => {
  //   const _value = e.target.value

  //   Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeLongitudeMetric()', {
  //     longitude_metric: _value
  //   })

  //   setLongitudeMetric(_value)
  // }

  const onChangeQuantity = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeQuantity()', { type: _value })
    setQuantity(_value)
  }

  const onChangeXSpacing = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeXSpacing()', { x_spacing: _value })
    setXSpacing(_value)
  }

  const onChangeYSpacing = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeYSpacing()', { y_spacing: _value })
    setYSpacing(_value)
  }

  const onChangeOrientation = (e) => {
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onChangeOrientation()', { orientation: _value })
    setOrientation(_value)
  }

  // ----

  const onCloseDialog = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onCloseDialog()')
    setOpenDialog(false)
  }

  useImperativeHandle(ref, () => ({
    async onSubmitForm() {
      const newLocation = {
        name: name === '' ? null : name,
        description: description === '' ? null : description,
        type: type === '' ? null : type,
        latitude: latitude === '' ? null : latitude,
        longitude: longitude === '' ? null : longitude,
        // x: latitude_metric === '' ? null : latitude_metric,
        // y: longitude_metric === '' ? null : longitude_metric,
        quantity: quantity === '' ? null : quantity,
        x_spacing: x_spacing === '' ? null : x_spacing,
        y_spacing: y_spacing === '' ? null : y_spacing,
        orientation: orientation === '' ? null : orientation // >= 0 && <= 360
      }

      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onSubmitForm()', {
        newLocation,
        location
      })

      const _onSuccess = (res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeLocationForm.onSubmitForm()._onSuccess()', { res })

        // -- close popin only in case of success
        onCloseDialog()
      }

      dispatchAction(newLocation, id, _onSuccess)
    }
  }))

  useEffect(() => {
    if (map.current) {
      return
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapBoxKeys.MAP_BLUE,
      center: [longitude, latitude],
      zoom: 9
    })

    const canvas = map.current.getCanvasContainer()

    const colors = {
      INITIAL_POSITION: '#F84C4C',
      IS_MOVING: '#3bb2d0',
      HAS_MOVED: '#3887be'
    }

    const onMove = (e) => {
      const coords = e.lngLat
      canvas.style.cursor = 'grabbing'
      geojson.features[0].geometry.coordinates = [coords.lng, coords.lat]
      map.current.getSource('point').setData(geojson)
    }

    const onUp = (e) => {
      const coords = e.lngLat
      setLongitude(coords.lng)
      setLatitude(coords.lat)

      canvas.style.cursor = ''

      map.current.off('mousemove', onMove)
      map.current.off('touchmove', onMove)
    }

    map.current.on('load', () => {
      map.current.addSource('point', {
        type: 'geojson',
        data: geojson
      })

      map.current.addLayer({
        id: 'point',
        type: 'circle',
        source: 'point',
        className: styleClasses.circleStyle,
        paint: {
          'circle-radius': 10,
          'circle-color': colors.INITIAL_POSITION
        }
      })

      map.current.on('mouseenter', 'point', () => {
        map.current.setPaintProperty('point', 'circle-color', colors.IS_MOVING)
        canvas.style.cursor = 'move'
      })

      map.current.on('mouseleave', 'point', () => {
        map.current.setPaintProperty('point', 'circle-color', colors.HAS_MOVED)
        canvas.style.cursor = ''
      })

      map.current.on('mousedown', 'point', (e) => {
        e.preventDefault()
        canvas.style.cursor = 'grab'

        map.current.on('mousemove', onMove)
        map.current.once('mouseup', onUp)
      })

      map.current.on('touchstart', 'point', (e) => {
        if (e.points.length !== 1) {
          return
        }

        e.preventDefault()

        map.current.on('touchmove', onMove)
        map.current.once('touchend', onUp)
      })
    })
  })

  Logger.trace(LoggerTypes.CONTAINER, 'AttributeLocationForm.render()', {
    location: {
      name,
      description,
      type,
      latitude,
      longitude,
      quantity,
      x_spacing,
      y_spacing,
      orientation
    },
    errors
  })

  return (
    <div className="AttributeForm AttributeLocationForm">
      <FormTitle title={title} icon={iconNames.LOCATION} iconSize={20} />
      <form>
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={6} sm={6} className="fieldContainer">
            {/*  name  */}
            <Grid item xs={12} sm={12}>
              <TextField
                type={textFieldTypes.TEXT}
                name="name"
                id="name"
                value={name}
                label={t('attributes.locations.name.label')}
                className="common_fullwidth"
                onChange={onChangeName}
                placeholder={t('attributes.locations.name.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.name)}
                helperText={(errors && errors?.name) || ''}
              />
            </Grid>

            {/*  description  */}
            <Grid item xs={12} sm={12}>
              <TextField
                type={textFieldTypes.TEXT}
                name="description"
                id="description"
                label={t('attributes.locations.description.label')}
                onChange={onChangeDescription}
                value={description}
                className="common_fullwidth"
                placeholder={t('attributes.locations.description.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.description)}
                helperText={(errors && errors?.description) || ''}
              />
            </Grid>

            {/* type */}
            <Grid item xs={12} sm={12} className="common_fullwidth">
              <FormControl error={Boolean(errors && errors?.type)}>
                <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                  {t('attributes.locations.type.label')}
                </InputLabel>
                <Select
                  className="common_fullwidth"
                  value={type}
                  defaultValue={locationsTypes.POINT}
                  onChange={onChangeType}
                  label=""
                  variant={variantTypes.OUTLINED}
                >
                  <MenuItem value={locationsTypes.POINT}>{t('attributes.locations.type.point')}</MenuItem>
                  <MenuItem value={locationsTypes.GRID}>{t('attributes.locations.type.grid')}</MenuItem>
                </Select>
                <FormHelperText>{(errors && errors?.type) || ''}</FormHelperText>
              </FormControl>

              {/*  quantity  */}
              {type === locationsTypes.GRID && (
                <TextField
                  style={{ width: '90px', marginLeft: '10px' }}
                  type={textFieldTypes.NUMBER}
                  name="quantity"
                  id="quantity"
                  value={quantity}
                  label={t('attributes.locations.quantity.label')}
                  onChange={onChangeQuantity}
                  placeholder={t('attributes.locations.quantity.placeholder')}
                  variant={variantTypes.OUTLINED}
                  error={Boolean(errors && errors?.quantity)}
                  helperText={(errors && errors?.quantity) || ''}
                />
              )}

              {/*  x_spacing  */}
              {type === locationsTypes.GRID && (
                <>
                  <TextField
                    style={{ width: '90px', marginLeft: '10px' }}
                    type={textFieldTypes.NUMBER}
                    name="x_spacing"
                    id="x_spacing"
                    value={x_spacing}
                    label={t('attributes.locations.x_spacing.label')}
                    onChange={onChangeXSpacing}
                    placeholder={t('attributes.locations.x_spacing.placeholder')}
                    variant={variantTypes.OUTLINED}
                    error={Boolean(errors && errors?.x_spacing)}
                    helperText={(errors && errors?.x_spacing) || ''}
                  />
                  <span className="spacingUnit">m</span>
                </>
              )}

              {/*  y_spacing  */}
              {type === locationsTypes.GRID && (
                <>
                  <TextField
                    style={{ width: '90px', marginLeft: '10px' }}
                    type={textFieldTypes.NUMBER}
                    name="y_spacing"
                    id="y_spacing"
                    value={y_spacing}
                    label={t('attributes.locations.y_spacing.label')}
                    onChange={onChangeYSpacing}
                    placeholder={t('attributes.locations.y_spacing.placeholder')}
                    variant={variantTypes.OUTLINED}
                    error={Boolean(errors && errors?.y_spacing)}
                    helperText={(errors && errors?.y_spacing) || ''}
                  />
                  <span className="spacingUnit">m</span>
                </>
              )}
            </Grid>

            {/* 
                latitude  (degrees / metric)
                longitude (degrees / metric)
            */}
            <Grid item xs={12} sm={12}>
              <TextField
                type={textFieldTypes.NUMBER}
                name="latitude"
                id="latitude"
                value={latitude}
                label={t('attributes.locations.latitude.label')}
                onChange={onChangeLatitude}
                style={{ width: '47%', marginRight: '3%' }}
                placeholder={t('attributes.locations.latitude.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.latitude)}
                helperText={(errors && errors?.latitude) || ''}
              />
              {/* <TextField
                type={textFieldTypes.NUMBER}
                name="latitude_metric"
                id="latitude_metric"
                value={latitude_metric}
                label={t('attributes.locations.latitude_metric.label')}
                onChange={onChangeLatitudeMetric}
                className="common_halfwidth"
                placeholder={t('attributes.locations.latitude_metric.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.x)}
                helperText={(errors && errors?.x) || ''}
              /> */}

              <TextField
                type={textFieldTypes.NUMBER}
                name="longitude"
                id="longitude"
                value={longitude}
                label={t('attributes.locations.longitude.label')}
                onChange={onChangeLongitude}
                style={{ width: '50%' }}
                placeholder={t('attributes.locations.longitude.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.longitude)}
                helperText={(errors && errors?.longitude) || ''}
              />
              {/* <TextField
                type={textFieldTypes.NUMBER}
                name="longitude_metric"
                id="longitude_metric"
                value={longitude_metric}
                label={t('attributes.locations.longitude_metric.label')}
                onChange={onChangeLongitudeMetric}
                className="common_halfwidth"
                placeholder={t('attributes.locations.longitude_metric.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.y)}
                helperText={(errors && errors?.y) || ''}
              /> */}
            </Grid>

            {/*  orientation  */}
            <Grid item xs={12} sm={12}>
              <TextField
                type={textFieldTypes.NUMBER}
                name="orientation"
                id="orientation"
                value={orientation}
                label={t('attributes.locations.orientation.label')}
                onChange={onChangeOrientation}
                onInput={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value, 10)).toString().slice(0, 12)
                }}
                InputProps={{ inputProps: { min: 0, max: 360 } }}
                className="common_fullwidth"
                placeholder={t('attributes.locations.orientation.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.orientation)}
                helperText={(errors && errors?.orientation) || ''}
              />
            </Grid>
          </Grid>

          {/*  MAP */}
          <Grid item={true} xs={6} sm={6}>
            <div className="map-container">
              <span className="coordinateXY">
                {t('attributes.locations.shortcut.lat')}: {latitude} | {t('attributes.locations.shortcut.lng')}:{' '}
                {longitude}
              </span>
              <div ref={mapContainer} className="map"></div>
            </div>
          </Grid>
        </Grid>
      </form>
    </div>
  )
})

AttributeLocationForm.displayName = 'AttributeLocationForm'

AttributeLocationForm.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  location: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
    quantity: PropTypes.number,
    x_spacing: PropTypes.number,
    y_spacing: PropTypes.number,
    orientation: PropTypes.number
  }).isRequired,
  errors: PropTypes.shape({
    name: PropTypes.any,
    description: PropTypes.any,
    type: PropTypes.any,
    latitude: PropTypes.any,
    longitude: PropTypes.any,
    x: PropTypes.any,
    y: PropTypes.any,
    quantity: PropTypes.any,
    x_spacing: PropTypes.any,
    y_spacing: PropTypes.any,
    orientation: PropTypes.any
  })
}

AttributeLocationForm.defaultProps = {
  setOpenDialog: () => {},
  dispatchAction: () => {},
  title: null,
  location: defaultValue,
  errors: null
}
